import { useEffect, useState } from "react";
import { Pencil, Trash2, Plus } from "lucide-react";
import { api, formatApiError } from "../../../lib/api";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { money } from "./common";

const EMPTY = {
    code: "", type: "percentage", value: "", buy_qty: 1, free_qty: 1, min_spend: "", starts_at: "", ends_at: "",
    applies_to: "all", product_ids: [], category_names: [], max_uses: "", active: true, show_on_homepage: true,
};

export default function DiscountsAdmin() {
    const [discounts, setDiscounts] = useState([]);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    const load = () => {
        api.get("/admin/discounts").then((r) => setDiscounts(r.data)).catch(() => {});
        api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
        api.get("/admin/categories").then((r) => setCategories(r.data)).catch(() => {});
    };
    useEffect(() => {
        load();
    }, []);

    const openEditor = (d) => {
        setEditing(d || null);
        setForm(
            d
                ? {
                      code: d.code, type: d.type, value: d.value, buy_qty: d.buy_qty ?? 1, free_qty: d.free_qty ?? 1,
                      min_spend: d.min_spend ?? "",
                      starts_at: d.starts_at ? d.starts_at.slice(0, 16) : "", ends_at: d.ends_at ? d.ends_at.slice(0, 16) : "",
                      applies_to: d.applies_to || "all", product_ids: d.product_ids || [],
                      category_names: d.category_names || [], max_uses: d.max_uses ?? "", active: d.active !== false,
                      show_on_homepage: d.show_on_homepage !== false,
                  }
                : EMPTY
        );
        setOpen(true);
    };

    const save = async () => {
        setSaving(true);
        const payload = {
            code: form.code, type: form.type, value: form.type === "bxgy" ? 0 : Number(form.value),
            buy_qty: form.type === "bxgy" ? Number(form.buy_qty) : null,
            free_qty: form.type === "bxgy" ? Number(form.free_qty) : null,
            min_spend: form.min_spend === "" ? null : Number(form.min_spend),
            starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
            ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
            applies_to: form.applies_to, product_ids: form.product_ids, category_names: form.category_names,
            max_uses: form.max_uses === "" ? null : Number(form.max_uses), active: form.active,
            show_on_homepage: form.show_on_homepage,
        };
        try {
            if (editing) {
                await api.put(`/admin/discounts/${editing.id}`, payload);
                toast.success("Discount updated");
            } else {
                await api.post("/admin/discounts", payload);
                toast.success("Discount created");
            }
            setOpen(false);
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const toggleList = (list, value) =>
        list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

    return (
        <div data-testid="admin-discounts">
            <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-slate-500">{discounts.length} discount codes</p>
                <Button onClick={() => openEditor(null)} data-testid="admin-add-discount" className="rounded-full bg-[#0033A0] hover:bg-[#002277]">
                    <Plus className="h-4 w-4 mr-2" /> Create Discount
                </Button>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100" data-testid="admin-discounts-list">
                {discounts.length === 0 && <p className="p-6 text-sm text-slate-400">No discounts yet.</p>}
                {discounts.map((d) => (
                    <div key={d.id} className="flex items-center gap-4 p-4 flex-wrap" data-testid={`discount-row-${d.code}`}>
                        <div className="flex-1 min-w-40">
                            <p className="font-heading font-extrabold text-[#0033A0] flex items-center gap-2">
                                {d.code}
                                {!d.active && <span className="rounded-full bg-slate-200 text-slate-600 text-xs px-2 py-0.5">Inactive</span>}
                            </p>
                            <p className="text-xs text-slate-500">
                                {d.type === "percentage" ? `${d.value}% off` : d.type === "bxgy" ? `Buy ${d.buy_qty} get ${d.free_qty} free` : `${money(d.value)} off`}
                                {d.applies_to !== "all" ? ` · ${d.applies_to === "products" ? `${d.product_ids.length} products` : d.category_names.join(", ")}` : " · everything"}
                                {d.min_spend ? ` · min spend ${money(d.min_spend)}` : ""}
                                {d.ends_at ? ` · ends ${new Date(d.ends_at).toLocaleDateString("en-GB")}` : ""}
                                {` · used ${d.usage_count}${d.max_uses ? `/${d.max_uses}` : ""} times`}
                            </p>
                        </div>
                        <Button variant="outline" size="sm" className="rounded-full" onClick={() => openEditor(d)} data-testid={`edit-discount-${d.code}`}>
                            <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" className="rounded-full text-red-600 border-red-200 hover:bg-red-50"
                            data-testid={`delete-discount-${d.code}`}
                            onClick={() => window.confirm(`Delete code ${d.code}?`) && api.delete(`/admin/discounts/${d.id}`).then(load).then(() => toast.success("Deleted"))}>
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                ))}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="discount-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">{editing ? "Edit Discount" : "Create Discount"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                                <Label htmlFor="df-code">Code *</Label>
                                <Input id="df-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                                    placeholder="e.g. FREDDIE10" data-testid="df-code" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Type</Label>
                                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                                    <SelectTrigger data-testid="df-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="percentage" data-testid="df-type-percentage">Percentage off</SelectItem>
                                        <SelectItem value="fixed" data-testid="df-type-fixed">Fixed amount off (£)</SelectItem>
                                        <SelectItem value="bxgy" data-testid="df-type-bxgy">Buy X Get Y Free</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            {form.type === "bxgy" ? (
                                <>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="df-buy-qty">Customer buys *</Label>
                                        <Input id="df-buy-qty" type="number" min="1" step="1" value={form.buy_qty}
                                            onChange={(e) => setForm({ ...form, buy_qty: e.target.value })} data-testid="df-buy-qty" />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="df-free-qty">Gets free *</Label>
                                        <Input id="df-free-qty" type="number" min="1" step="1" value={form.free_qty}
                                            onChange={(e) => setForm({ ...form, free_qty: e.target.value })} data-testid="df-free-qty" />
                                    </div>
                                </>
                            ) : (
                                <div className="space-y-1.5">
                                    <Label htmlFor="df-value">{form.type === "percentage" ? "Percentage" : "Amount £"} *</Label>
                                    <Input id="df-value" type="number" min="0" step="0.01" value={form.value}
                                        onChange={(e) => setForm({ ...form, value: e.target.value })} data-testid="df-value" />
                                </div>
                            )}
                            <div className="space-y-1.5">
                                <Label htmlFor="df-min">Minimum spend £</Label>
                                <Input id="df-min" type="number" min="0" step="0.01" value={form.min_spend}
                                    onChange={(e) => setForm({ ...form, min_spend: e.target.value })} placeholder="None" data-testid="df-min-spend" />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="df-start">Starts</Label>
                                <Input id="df-start" type="datetime-local" value={form.starts_at}
                                    onChange={(e) => setForm({ ...form, starts_at: e.target.value })} data-testid="df-starts" />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="df-end">Ends</Label>
                                <Input id="df-end" type="datetime-local" value={form.ends_at}
                                    onChange={(e) => setForm({ ...form, ends_at: e.target.value })} data-testid="df-ends" />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Applies to</Label>
                                <Select value={form.applies_to} onValueChange={(v) => setForm({ ...form, applies_to: v })}>
                                    <SelectTrigger data-testid="df-applies"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Whole basket</SelectItem>
                                        <SelectItem value="products">Specific products</SelectItem>
                                        <SelectItem value="categories">Specific categories</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="df-max">Max uses</Label>
                                <Input id="df-max" type="number" min="0" value={form.max_uses}
                                    onChange={(e) => setForm({ ...form, max_uses: e.target.value })} placeholder="Unlimited" data-testid="df-max-uses" />
                            </div>
                        </div>
                        {form.applies_to === "products" && (
                            <div className="space-y-2 max-h-48 overflow-y-auto border border-slate-200 rounded-xl p-3" data-testid="df-products">
                                {products.map((p) => (
                                    <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                                        <input type="checkbox" checked={form.product_ids.includes(p.id)}
                                            onChange={() => setForm({ ...form, product_ids: toggleList(form.product_ids, p.id) })}
                                            className="accent-[#0033A0]" data-testid={`df-product-${p.id}`} />
                                        {p.name}
                                    </label>
                                ))}
                            </div>
                        )}
                        {form.applies_to === "categories" && (
                            <div className="space-y-2 border border-slate-200 rounded-xl p-3" data-testid="df-categories">
                                {categories.map((c) => (
                                    <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                                        <input type="checkbox" checked={form.category_names.includes(c.name)}
                                            onChange={() => setForm({ ...form, category_names: toggleList(form.category_names, c.name) })}
                                            className="accent-[#0033A0]" data-testid={`df-category-${c.id}`} />
                                        {c.name}
                                    </label>
                                ))}
                            </div>
                        )}
                        <label className="flex items-center gap-2 text-sm font-bold text-slate-700 cursor-pointer">
                            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })}
                                className="accent-[#0033A0]" data-testid="df-active" />
                            Active
                        </label>
                        <label className="flex items-center gap-2 text-sm font-bold text-slate-700 cursor-pointer">
                            <input type="checkbox" checked={form.show_on_homepage} onChange={(e) => setForm({ ...form, show_on_homepage: e.target.checked })}
                                className="accent-[#0033A0]" data-testid="df-show-on-homepage" />
                            Show on homepage banner
                        </label>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
                        <Button onClick={save} disabled={saving} className="rounded-full bg-[#0033A0] hover:bg-[#002277]" data-testid="df-save">
                            {saving ? "Saving…" : "Save Discount"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
